import useDocumentAttachEvent from 'hooks/documentAttachEventHook';
import { EVENT } from 'static/Constants';

import { useIsMutating, useMutation } from '@tanstack/react-query';

import { dialogFunction } from 'common/components';

import { useGetMemberId } from 'hooks';
import { mutationSendIXOneCaptureKeys } from '../ixone-section/hooks';
import {
  mutationProductActiveKeys,
  useValidateActiveProduct,
} from '../make-product-active/hooks';
import { useQueryBillingProperties } from 'pages/check-credit-card-info/queries';

import { chargeSavedMemberCard } from 'services/creditCardEula';
import { checkCODTerm, checkInvoicedTerm, checkMonthlyTerm } from './utils';
import {
  useQueryBillingNote,
  useQueryMemberDiscountProperties,
  useRefetchAllMemberBillingStatement,
} from 'pages/company-profile/components/tabs/billing/queries';

export const useValidatingEulaCard = ({
  handleAddCreditCard,
  dependencies,
}) => {
  const onAcceptCreditCardEula = (e) => {
    const when = e?.detail?.when;

    const actions = {
      'add-card': handleAddCreditCard,
    };

    actions[when]?.();
  };

  useDocumentAttachEvent({
    name: EVENT.ACCEPT_CARD_EULA,
    handler: onAcceptCreditCardEula,
    dependencies,
  });
};

export const mutationPayNowKeys = ['payment', 'cod', 'pay-now'];

export const usePayNowSavedMemberCard = ({ onCancel } = {}) => {
  const { memberId } = useGetMemberId();

  const refetchAllMemberBillingStatement =
    useRefetchAllMemberBillingStatement();

  const { handleRevalidateActiveProduct } = useValidateActiveProduct({
    productIds: [],
    enabled: false,
  });

  const { invalidateMemberDiscountProperties } =
    useQueryMemberDiscountProperties({ memberId, enabled: false });
  const { invalidateBillingNote } = useQueryBillingNote({
    memberId,
    enabled: false,
  });

  return useMutation({
    mutationFn: chargeSavedMemberCard,
    mutationKey: mutationPayNowKeys,
    onSuccess: async (result) => {
      if (result?.isSuccess) {
        // CustomNotification.success('Transition completed successfully!');
      } else {
        const errorMsg = result?.message ?? 'Something went wrong';
        dialogFunction({
          type: 'warn',
          content: errorMsg,
          okText: 'OK',
          cancelButtonProps: { style: { display: 'none' } },
          onOk: () => {
            onCancel(); //* close charge dialog
          },
        });
      }
      refetchAllMemberBillingStatement();
      invalidateMemberDiscountProperties();
      invalidateBillingNote();
      handleRevalidateActiveProduct();
    },
    onError: () => {},
  });
};

export const useCheckPaymentTerm = () => {
  const { memberId } = useGetMemberId();

  const { billingProperties, isLoading } = useQueryBillingProperties({
    memberId,
  });

  return {
    isCODTerm: checkCODTerm(billingProperties?.billingTerms),
    isInvoicedTerm: checkInvoicedTerm(billingProperties?.billingTerms),
    isMonthlyTerm: checkMonthlyTerm(billingProperties?.billingTerms),
    isLoadingBillingProperties: isLoading,
  };
};

export const useSubmittingCODPayment = () => {
  const isLoadingActiveProduct =
    useIsMutating({
      mutationKey: mutationProductActiveKeys,
    }) > 0;

  const isLoadingIXOne =
    useIsMutating({
      mutationKey: mutationSendIXOneCaptureKeys,
    }) > 0;

  const isLoadingPayNow =
    useIsMutating({
      mutationKey: mutationPayNowKeys,
    }) > 0;

  return isLoadingActiveProduct || isLoadingIXOne || isLoadingPayNow;
};
